import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import useEnhancedPremiumStatus from '../../hooks/useEnhancedPremiumStatus';
import { USER_PROFILE_QUERY_KEY } from '../../hooks/useUserProfile';
import { redeemPartnerCode } from '../../services/partnerCodeService';
import { assignPrimaryRole } from '../../services/roleAssignmentService';
import {
  mapPremiumStatusToAccountSubscription,
  shouldShowPartnerRedeemField,
  getPartnerSubscriptionPlanAction,
  navigateToSubscriptionPage,
} from '../../utils/partnerAccessExpiry';

const PLAN_BADGE_LABELS = {
  trial: 'Free Trial',
  monthly: 'Pro Monthly',
  annual: 'Pro Annual',
  partner: 'Partner Access',
  free: 'Free',
};

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatMonthYear(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatRenewalDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

function daysRemaining(endDate) {
  const diff = endDate.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function getStatusLine(subscription) {
  const { plan, status, trialEndsAt, currentPeriodEnd, partnerProUntil } = subscription;
  const periodEnd = toDate(currentPeriodEnd);
  const trialEnd = toDate(trialEndsAt);
  const partnerEnd = toDate(partnerProUntil);

  if (plan === 'partner' && partnerEnd) {
    return `Active until ${formatRenewalDate(partnerEnd)}`;
  }

  if (status === 'canceled') {
    return periodEnd
      ? `Canceled. Access until ${formatRenewalDate(periodEnd)}`
      : 'Canceled. Access until period end';
  }

  if (plan === 'trial' && trialEnd) {
    const days = daysRemaining(trialEnd);
    return `${days} day${days === 1 ? '' : 's'} remaining`;
  }

  if ((plan === 'monthly' || plan === 'annual') && periodEnd && (status === 'active' || status === 'past_due')) {
    return `Renews ${formatRenewalDate(periodEnd)}`;
  }

  if (plan === 'free') {
    return 'No active subscription';
  }

  return 'No active subscription';
}

function getPlanAction(plan, status, { partnerProUntil } = {}) {
  if (status === 'canceled') {
    return { label: 'Resubscribe', variant: 'filled-primary' };
  }

  switch (plan) {
    case 'trial':
      return { label: 'Upgrade Now', variant: 'filled-primary' };
    case 'monthly':
      return { label: 'Switch to Annual · Save 30%', variant: 'outline' };
    case 'annual':
      return { label: 'Manage Plan', variant: 'outline' };
    case 'partner':
      return getPartnerSubscriptionPlanAction(partnerProUntil);
    case 'free':
    default:
      return { label: 'View Plans', variant: 'filled-primary' };
  }
}

function SettingsAccountSectionTitle({ title }) {
  return <h3 className="settings-page-account__subsection-heading">{title}</h3>;
}

function AccountProfileRow({ label, value }) {
  return (
    <div className="settings-page-account__profile-row">
      <span className="settings-page-account__profile-row-label">{label}</span>
      <span className="settings-page-account__profile-row-value">{value}</span>
    </div>
  );
}

function PartnerCodeRedeemField({ userId, onRedeemed }) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRedeem = async (event) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter a partner code to redeem.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const result = await redeemPartnerCode(trimmed);
      setCode('');
      setExpanded(false);
      await onRedeemed(result);
    } catch (redeemError) {
      setError(redeemError.message || 'Could not redeem this code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="settings-page-account__partner-redeem">
      {!expanded ? (
        <button
          type="button"
          className="settings-page-account__partner-redeem-toggle"
          onClick={() => setExpanded(true)}
        >
          Have a partner code?
        </button>
      ) : (
        <form className="settings-page-account__partner-redeem-form" onSubmit={handleRedeem}>
          <label className="settings-page-account__partner-redeem-label" htmlFor={`partner-code-${userId}`}>
            Partner code
          </label>
          <div className="settings-page-account__partner-redeem-row">
            <input
              id={`partner-code-${userId}`}
              type="text"
              className="settings-page-account__partner-redeem-input"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                if (error) setError('');
              }}
              placeholder="e.g. BOOTCAMP-FALL26"
              autoComplete="off"
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className="settings-page-account__btn settings-page-account__btn--filled-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Redeeming…' : 'Redeem'}
            </button>
          </div>
          {error ? (
            <p className="settings-page-account__partner-redeem-error" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
}

function AccountSettings({ userId }) {
  const queryClient = useQueryClient();
  const [authUser, setAuthUser] = useState(() => getAuth().currentUser);
  const {
    isPremium,
    hasPaidSubscription,
    planType,
    partnerProUntil,
    partnerCode,
    partnerName,
    subscriptionData,
    isLoading: isLoadingSubscription,
    refreshPremiumStatus,
  } = useEnhancedPremiumStatus();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => setAuthUser(user));
    return unsubscribe;
  }, []);

  const subscription = useMemo(
    () => mapPremiumStatusToAccountSubscription({
      isPremium,
      hasPaidSubscription,
      planType,
      subscriptionData,
      partnerProUntil,
      partnerName,
      partnerCode,
    }),
    [isPremium, hasPaidSubscription, planType, subscriptionData, partnerProUntil, partnerName, partnerCode]
  );

  const handlePartnerRedeemed = async (result) => {
    refreshPremiumStatus();

    const roleId = result?.recommendedRoleId?.trim();

    if (userId && roleId) {
      try {
        await assignPrimaryRole(userId, roleId);
      } catch (err) {
        console.warn('[AccountSettings] assignPrimaryRole after partner redeem failed', err);
      }
    }

    if (userId) {
      queryClient.invalidateQueries({ queryKey: [USER_PROFILE_QUERY_KEY, userId] });
    }
  };

  const creationDate = authUser?.metadata?.creationTime
    ? new Date(authUser.metadata.creationTime)
    : null;

  const memberSince = creationDate ? formatMonthYear(creationDate) : '-';
  const displayName = authUser?.displayName?.trim() || '-';
  const email = authUser?.email || '-';

  const { plan, status } = subscription;
  const planBadgeLabel = plan === 'partner' && subscription.partnerName
    ? subscription.partnerName
    : (PLAN_BADGE_LABELS[plan] ?? PLAN_BADGE_LABELS.free);
  const statusLine = getStatusLine(subscription);
  const planAction = getPlanAction(plan, status, { partnerProUntil });
  const showDangerZone = (plan === 'monthly' || plan === 'annual') && status === 'active';
  const showUpsell = plan === 'trial' || plan === 'free';
  const showPartnerRedeem = shouldShowPartnerRedeemField(plan);

  const planBadgeClassName = [
    'settings-page-account__plan-badge',
    `settings-page-account__plan-badge--${plan in PLAN_BADGE_LABELS ? plan : 'free'}`
  ].join(' ');

  const actionButtonClassName = planAction
    ? [
      'settings-page-account__btn',
      planAction.variant === 'outline'
        ? 'settings-page-account__btn--outline'
        : 'settings-page-account__btn--filled-primary'
    ].join(' ')
    : '';

  return (
    <div className="settings-page-account">
      <section className="settings-page-account__subsection settings-page-account__subsection--profile">
        <div className="settings-page-account__profile-rows">
          <AccountProfileRow label="Display name" value={displayName} />
          <AccountProfileRow label="Email" value={email} />
          <AccountProfileRow label="Member since" value={memberSince} />
        </div>
      </section>

      <div className="settings-page-account__divider" aria-hidden="true" />

      <section className="settings-page-account__subsection">
        <SettingsAccountSectionTitle title="Subscription" />

        {isLoadingSubscription ? (
          <div className="settings-page-account__plan-skeleton" aria-hidden="true" />
        ) : (
          <>
            <div className="settings-page-account__plan-card">
              <div className="settings-page-account__plan-card-body">
                <div className="settings-page-account__plan-card-main">
                  <span className={planBadgeClassName}>{planBadgeLabel}</span>
                  <span className="settings-page-account__plan-status">{statusLine}</span>
                </div>
                {planAction && (
                  <div className="settings-page-account__plan-card-action">
                    <button
                      type="button"
                      className={actionButtonClassName}
                      onClick={navigateToSubscriptionPage}
                    >
                      {planAction.label}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {showPartnerRedeem && (
              <PartnerCodeRedeemField userId={userId} onRedeemed={handlePartnerRedeemed} />
            )}

            {showDangerZone && (
              <div className="settings-page-account__danger-zone">
                {!showCancelConfirm ? (
                  <button
                    type="button"
                    className="settings-page-account__cancel-link"
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    Cancel subscription
                  </button>
                ) : (
                  <div className="settings-page-account__cancel-confirm">
                    <p className="settings-page-account__cancel-confirm-text">
                      Are you sure? This will cancel at period end.
                    </p>
                    <div className="settings-page-account__cancel-confirm-actions">
                      <button
                        type="button"
                        className="settings-page-account__cancel-confirm-yes"
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        Yes, cancel
                      </button>
                      <button
                        type="button"
                        className="settings-page-account__cancel-confirm-dismiss"
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        Never mind
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showUpsell && (
              <div className="settings-page-account__upsell">
                <p className="settings-page-account__upsell-text">
                  Annual plan saves 30% vs monthly
                </p>
                <button
                  type="button"
                  className="settings-page-account__btn settings-page-account__btn--outline"
                  onClick={navigateToSubscriptionPage}
                >
                  See Plans
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export default AccountSettings;
